import { defaultLength } from '.'
import { blake2bHex } from 'blakejs'

/**
 * I want to write a few words on why `getIdempotentId` is needed.
 *
 * Replicache mutators run both in client (optimistic, fast run) and in the server. For this reasons,
 * mutations must be fully idempotent. This means that if a mutation is ran twice, the result must be
 * the same.
 *
 * For example, a mutation that creates a new block needs a block ID. To keep the mutation idempotent,
 * we generate the ID client side and send it as a parameter to the mutation. This is simple enough.
 *
 * But what happens if we don't exactly know how many entities (blocks or otherwise) will be created?
 * For example, we don't know how many blocks will a `commit` mutation create. We don't know how many
 * blocks will a `syncInstanceWithComponent` mutation create. Etc.
 *
 * This function generates a new, unique ID, based on a seed passed to it. It uses a hash function to
 * generate the ID, so it is deterministic. This trait comes with a big risk: parallel work might result
 * in the same ID being generated. See visual example here: ./idempotent-ids.excalidraw
 *
 * The solution to this is "splitting paths". For parallel work, we need each iteration to split the "hash destiny"
 * and create space to work without risk of collision. This is done by passing a `splitPaths` parameter.
 *
 * Another popular solution up until now is doing a reassign of the seed at every iteration. This should be
 * fine in most cases, but it sometimes does cause collisions, and if so, it's an incredibly hard issue to debug.
 * Nested loops are a common source of this issue.
 */

/**
 * RULE OF THUMB: if you're sending an idSeed parameter, you DON'T reassign, but rather create a splitPath.
 * But if you're using the idSeed to create a new entity, you DO reassign. Example:
 *
 * @example
 * const newBlockId = (idSeed = getIdSeed({ idSeed })) // do reassign
 * createBlock(tx, {
 *   id: newBlockId,
 *   idSeed: getIdSeed({ idSeed, splitPaths: { newPath: "<a-unique-new-path-like-${newBlockId}>" } }) // don't reassign
 * })
 */

export function getIdempotentId({
  idSeed,
  length = defaultLength,
  splitPaths,
}: {
  idSeed: string
  length?: number
  /**
   * If passed, the id will be split off to this hash path.
   * Useful for when you want to further prevent collisions in cases of nested loops.
   */
  splitPaths?: {
    newPath: string
  }
}) {
  // hash the idSeed
  // this will ensure the id is off to another path

  const hashTarget = `${
    splitPaths?.newPath ? `${splitPaths.newPath}/` : ''
  }${idSeed}`

  const generatedHash = blake2bHex(hashTarget)
  return generatedHash.slice(0, length)
}

export const addToStringButKeepSameLength = (str: string, toAdd: string) => {
  const length = str.length
  const toAddLength = toAdd.length

  if (toAddLength > length) {
    throw new Error('toAdd is longer than str')
  }

  return str.slice(0, length - toAddLength) + toAdd
}
